import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Firebase
import { db } from '../../firebase/config';
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    getDoc,
    updateDoc, 
    collectionGroup
} from 'firebase/firestore';

// Componentes y utilidades del sistema
import { EstadoCotizacionArt } from '../../data/listasDesplegables';
import { getCotizacionColor } from '../../utils/styles';
import { saveToFirestore } from '../../firebase/cleanPayload';

// Importamos el ecosistema centralizado de Seguros (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { parseCurrency, parsePercent, formatCurrencyForInput, normalizeString } from '../../utils/segurosUtils';
import { CurrencyInput, PercentInput } from '../../utils/FormInputs';

// --- ICONOS ---
const IconArrowLeft = () => <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IconPlus = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.6"><path d="M12 4v16m8-8H4"/></svg>;

// Helpers internos específicos del flujo de cotización
const formatPercentForInput = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return String(value).replace('.', ',');
};

const generarNumeroCotizacion = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `COT-${result}`; 
};

/**
 * FormularioCotizacionArt - Gestión y Negociación de Cotizaciones de ART
 */
const FormularioCotizacionArt = ({ entityData: propsEntity, isProspect: propsIsProspect, onSuccess, onCancel, initialData: propsInitialData = null }) => {
    const location = useLocation(); 
    const navigate = useNavigate();
    
    const stateData = location.state || {};
    const isFromEndoso = !!stateData.fromEndoso;
    const isEditMode = !!stateData.isEdit; 

    // Consumimos el contexto global de seguros
    const { dbMasters } = useSegurosData();
    
    // Identidad del Cliente
    const clientInfo = propsEntity || (isFromEndoso ? {
        id: stateData.clienteId,
        nombre: stateData.clienteNombre || 'Cliente',
        apellido: stateData.clienteApellido || '',
        cuit: stateData.clienteCuit || '',
        dni: stateData.clienteDni || '',
        persona: stateData.persona || 'FISICA',
        tipoEntidad: stateData.tipoEntidad || 'CLIENTE',
        canal: stateData.canal || '',
        canalNombre: stateData.canalNombre || '',
        canalNombreId: stateData.canalNombreId || '',
        subcanalNombre: stateData.subcanalNombre || '',
        subcanalNombreId: stateData.subcanalNombreId || ''
    } : null);

    const isProspect = propsIsProspect || stateData.tipoEntidad === 'PROSPECTO' || clientInfo?.tipoEntidad === 'PROSPECTO';

    const defaultFormData = {
        numeroCotizacion: '', 
        contratoOrigenId: '', 
        nContratoOrigen: '',  
        nContrato: '',
        contratoVinculadoId: '', 
        nContratoVinculado: '', 
        fechaCarga: new Date().toISOString().split('T')[0],
        tipoGestion: '',
        afiliacion: false,
        capitas: '',
        masaSalarial: '',
        alicuotaActual: '',
        artActual: '',
        artActualId: '',
        vendedor: '', 
        responsableAsignado: '',
        ciiu: '',
        estado: 'Pendiente', 
        observaciones: '',
        endosoVinculadoId: '',
        canal: '',
        canalNombre: '',
        canalNombreId: '',
        subcanalNombre: '',
        subcanalNombreId: '',
        // CAMPOS DENORMALIZADOS DEL AUMENTO
        mesAumento: '',
        periodoArca: '',
        alicuotaAumentada: '',
        porcentajeAumento: '',
        numeroEndoso: ''
    };

    const [formData, setFormData] = useState(defaultFormData);
    const [cotizaciones, setCotizaciones] = useState([{ compania: '', companiaId: '', estadoCotizacion: 'Cotizada', alicuotaCotizada: '', tieneFechaBloqueo: false, fechaBloqueo: '' }]);
    const [listaContratosCliente, setListaContratosCliente] = useState([]); 
    const [isLoadingContratos, setIsLoadingContratos] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [prevGestiones, setPrevGestiones] = useState([]);
    const [showImportSelector, setShowImportSelector] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [dbSubcanales, setDbSubcanales] = useState([]);

    // --- MEMORIZACIÓN DE MAESTROS DESDE EL CONTEXTO GLOBAL ---
    const companiasART = useMemo(() => {
        return (dbMasters?.companias || [])
            .filter(d => d.grupo === 'ART' || d.riesgo === 'ART')
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    }, [dbMasters?.companias]);

    const vendedores = useMemo(() => {
        return (dbMasters?.colaboradores || [])
            .map(d => d.nombre)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    }, [dbMasters?.colaboradores]);

    // CARGA DE CONTRATOS ESPECÍFICOS DEL CLIENTE
    useEffect(() => {
        const fetchContratos = async () => {
            const targetId = clientInfo?.id || propsInitialData?.clienteId;
            if (!isProspect && targetId) {
                setIsLoadingContratos(true);
                try {
                    const snap = await getDocs(collection(db, `public/data/clientes/${targetId}/contratos`));
                    setListaContratosCliente(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                } catch (e) {
                    console.error("Error cargando contratos vinculados:", e);
                } finally {
                    setIsLoadingContratos(false);
                }
            }
        };
        fetchContratos();
    }, [clientInfo?.id, propsInitialData?.clienteId, isProspect]);

    // CARGA DE DATOS EXISTENTES (EDICIÓN O ENDOSO CON AUTO-REPARACIÓN ROBUSTA)
    useEffect(() => {
        const cargarDatosExistentes = async () => {
            const cotId = stateData.cotizacionId || propsInitialData?.id;
            const cliId = clientInfo?.id || propsInitialData?.clienteId;

            if ((isEditMode || propsInitialData) && cotId && cliId) {
                try {
                    const path = `public/data/${isProspect ? 'prospectos' : 'clientes'}/${cliId}/cotizaciones_art/${cotId}`;
                    const docSnap = await getDoc(doc(db, path));
                    
                    if (docSnap.exists()) {
                        const data = docSnap.data();

                        // Obtenemos los valores existentes evitando strings vacíos o guiones
                        let contratoNum = (data.nContrato && data.nContrato !== '-') ? data.nContrato : ((data.nContratoOrigen && data.nContratoOrigen !== '-') ? data.nContratoOrigen : '');
                        let endosoNum = (data.nEndoso && data.nEndoso !== '-') ? data.nEndoso : ((data.numeroEndoso && data.numeroEndoso !== '-') ? data.numeroEndoso : '');
                        let contratoId = data.contratoOrigenId || '';
                        let endosoId = data.endosoVinculadoId || '';

                        // AUTO-REPARACIÓN: Si falta nContrato o nEndoso, buscamos el aumento vinculado en Firestore
                        if (!contratoNum || !endosoNum) {
                            try {
                                // 1. Intentamos buscar por endosoVinculadoId si existe
                                if (endosoId) {
                                    const qEndoso = query(collectionGroup(db, 'aumentos_art'), where('__name__', '==', endosoId));
                                    const snapEndoso = await getDocs(qEndoso);
                                    if (!snapEndoso.empty) {
                                        const endData = snapEndoso.docs[0].data();
                                        if (!endosoNum) endosoNum = endData.numeroEndoso || endData.nEndoso || '';
                                        if (!contratoNum) contratoNum = endData.nContrato || '';
                                        if (!contratoId) contratoId = snapEndoso.docs[0].ref.parent.parent?.id || endData.contratoId || '';
                                    }
                                }

                                // 2. Si todavía falta, buscamos el aumento que tenga asociada esta cotización
                                if (!endosoNum || !contratoNum) {
                                    const qPorCot = query(collectionGroup(db, 'aumentos_art'), where('cotizacionVinculadaId', '==', cotId));
                                    const snapPorCot = await getDocs(qPorCot);
                                    if (!snapPorCot.empty) {
                                        const endDoc = snapPorCot.docs[0];
                                        const endData = endDoc.data();
                                        endosoId = endDoc.id;
                                        if (!endosoNum) endosoNum = endData.numeroEndoso || endData.nEndoso || '';
                                        if (!contratoNum) contratoNum = endData.nContrato || '';
                                        if (!contratoId) contratoId = endDoc.ref.parent.parent?.id || endData.contratoId || '';
                                    }
                                }
                            } catch (errAuto) {
                                console.error("Error en auto-recuperación de aumento vinculado:", errAuto);
                            }
                        }

                        setFormData({
                            ...defaultFormData,
                            ...data,
                            endosoVinculadoId: endosoId,
                            contratoOrigenId: contratoId,
                            nContrato: contratoNum,
                            nContratoOrigen: contratoNum,
                            numeroEndoso: endosoNum, // Mantiene el valor para el renderizado local
                            afiliacion: data.afiliacion || false,
                            masaSalarial: formatCurrencyForInput(data.masaSalarial),
                            alicuotaActual: formatPercentForInput(data.alicuotaActual),
                            fechaCarga: data.fechaCarga?.seconds 
                                ? new Date(data.fechaCarga.seconds * 1000).toISOString().split('T')[0]
                                : data.fechaCarga || new Date().toISOString().split('T')[0]
                        });

                        if (data.cotizaciones) {
                            setCotizaciones(data.cotizaciones.map(c => ({
                                ...c,
                                estadoCotizacion: c.estadoCotizacion || 'Cotizada',
                                alicuotaCotizada: formatPercentForInput(c.alicuotaCotizada)
                            })));
                        }

                        if (contratoId) buscarNegociacionesPrevias(contratoId);
                        return;
                    }
                } catch (e) {
                    console.error("Error al recuperar cotización para editar:", e);
                }
            }

            if (isFromEndoso) {
                let resolvedCanalId = stateData.canalNombreId || '';
                if (!resolvedCanalId && stateData.canalNombre && dbMasters.canales) {
                    const foundC = dbMasters.canales.find(c => 
                        normalizeString(c.nombreDisplay || c.nombre) === normalizeString(stateData.canalNombre)
                    );
                    if (foundC) resolvedCanalId = foundC.id;
                }

                const contratoNumero = stateData.nContrato || stateData.nContratoOrigen || '';
                const endosoNumero = stateData.numeroEndoso || stateData.nEndoso || '';

                setFormData({
                    ...defaultFormData,
                    numeroCotizacion: generarNumeroCotizacion(),
                    tipoGestion: 'Aumento de Tarifa',
                    contratoOrigenId: stateData.contratoId || '',
                    nContratoOrigen: contratoNumero,
                    nContrato: contratoNumero,
                    contratoVinculadoId: '', 
                    nContratoVinculado: '',  
                    capitas: stateData.capitas || '',
                    masaSalarial: formatCurrencyForInput(stateData.masaSalarial),
                    alicuotaActual: formatPercentForInput(stateData.alicuotaActual),
                    artActual: stateData.compania || '',
                    artActualId: stateData.companiaId || '', 
                    vendedor: stateData.vendedor || '', 
                    responsableAsignado: stateData.responsableAsignado || '',
                    ciiu: stateData.ciiu || '', 
                    endosoVinculadoId: stateData.endosoVinculadoId || stateData.id || '',
                    canal: stateData.canal || '',
                    canalNombre: stateData.canalNombre || '',
                    canalNombreId: resolvedCanalId, 
                    subcanalNombre: stateData.subcanalNombre || '',
                    subcanalNombreId: stateData.subcanalNombreId || '',
                    mesAumento: stateData.mesAumento || '',
                    periodoArca: stateData.periodoArca || '',
                    alicuotaAumentada: stateData.alicuotaAumentada || '',
                    porcentajeAumento: stateData.porcentajeAumento || '',
                    numeroEndoso: endosoNumero
                });
                if (stateData.contratoId) buscarNegociacionesPrevias(stateData.contratoId);
            } else if (!isEditMode && !propsInitialData) {
                setFormData(p => ({
                    ...p,
                    numeroCotizacion: generarNumeroCotizacion(),
                    vendedor: clientInfo?.vendedor || '',
                    responsableAsignado: clientInfo?.responsableAsignado || '', 
                    canal: clientInfo?.canal || '',
                    canalNombre: clientInfo?.canalNombre || '',
                    canalNombreId: clientInfo?.canalNombreId || '',
                    subcanalNombre: clientInfo?.subcanalNombre || '',
                    subcanalNombreId: clientInfo?.subcanalNombreId || ''
                }));
            }
        };

        cargarDatosExistentes();
    }, [propsInitialData, stateData.cotizacionId, isEditMode, isFromEndoso, dbMasters.canales]);

    // SINCRONIZACIÓN RETROACTIVA DE ID DE ASEGURADORA ACTUAL
    useEffect(() => {
        if (companiasART.length > 0 && formData.artActual && !formData.artActualId) {
            const found = companiasART.find(c => normalizeString(c.nombre) === normalizeString(formData.artActual));
            if (found) {
                setFormData(prev => ({ ...prev, artActualId: found.id }));
            }
        }
    }, [companiasART, formData.artActual, formData.artActualId]);

    // SINCRONIZACIÓN RETROACTIVA DE COMPAÑÍAS COTIZADAS
    useEffect(() => {
        if (companiasART.length > 0 && cotizaciones.length > 0) {
            let changed = false;
            const updated = cotizaciones.map(c => {
                if (c.compania && !c.companiaId) {
                    const found = companiasART.find(cia => normalizeString(cia.nombre) === normalizeString(c.compania));
                    if (found) {
                        changed = true;
                        return { ...c, companiaId: found.id };
                    }
                }
                return c;
            });
            if (changed) setCotizaciones(updated);
        }
    }, [companiasART, cotizaciones]);

    // CARGAMOS LA LISTA DE SUBCANALES SEGÚN EL CANAL SELECCIONADO
    useEffect(() => {
        const fetchSubcanales = async () => {
            if (!formData.canalNombreId) {
                setDbSubcanales([]);
                return;
            }
            try {
                const snap = await getDocs(query(collection(db, 'public/data/subcanales'), where('canalId', '==', formData.canalNombreId)));
                setDbSubcanales(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (error) {
                console.error("Error al obtener subcanales para el canal seleccionado:", error);
            }
        };
        fetchSubcanales();
    }, [formData.canalNombreId]);

    // SINCRONIZACIÓN RETROACTIVA DE SUBCANAL ASOCIADO
    useEffect(() => {
        if (isFromEndoso && dbSubcanales.length > 0 && !formData.subcanalNombreId && stateData.subcanalNombre) {
            const foundSub = dbSubcanales.find(s => 
                normalizeString(s.nombre) === normalizeString(stateData.subcanalNombre)
            );
            if (foundSub) {
                setFormData(prev => ({
                    ...prev,
                    subcanalNombreId: foundSub.id
                }));
            }
        }
    }, [dbSubcanales, isFromEndoso, formData.subcanalNombreId, stateData.subcanalNombre]);

    const buscarNegociacionesPrevias = async (contratoId) => {
        if (!contratoId) return;
        try {
            const q = query(collectionGroup(db, 'cotizaciones_art'), where('contratoOrigenId', '==', contratoId));
            const snap = await getDocs(q);
            const todas = snap.docs.flatMap(d => (d.data().cotizaciones || []).map(cot => ({
                ...cot, 
                parentFecha: d.data().fechaCarga, 
                parentTipo: d.data().tipoGestion, 
                parentCot: d.data().numeroCotizacion
            })));
            const ordenadas = todas.filter(o => parseFloat(String(o.alicuotaCotizada).replace(',','.')) > 0 || o.estadoCotizacion === 'Bloqueada');
            setPrevGestiones(ordenadas.sort((a, b) => new Date(b.parentFecha) - new Date(a.parentFecha)));
        } catch (e) { 
            console.error("Error al buscar deudas previas:", e); 
        }
    };

    const handleSelectOferta = (oferta) => {
        const nuevaCot = {
            compania: oferta.compania,
            companiaId: oferta.companiaId || '',
            alicuotaCotizada: formatPercentForInput(oferta.alicuotaCotizada),
            estadoCotizacion: oferta.estadoCotizacion || 'Cotizada',
            tieneFechaBloqueo: !!oferta.tieneFechaBloqueo,
            fechaBloqueo: oferta.fechaBloqueo || ''
        };
        setCotizaciones(prev => (prev.length === 1 && !prev[0].compania) ? [nuevaCot] : [...prev, nuevaCot]);
    };

    const handleCancel = () => onCancel ? onCancel() : navigate(-1);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const val = type === 'checkbox' ? checked : value;
            const newState = { ...prev, [name]: val };
            
            // Resetear afiliación si cambia el tipo de gestión
            if (name === 'tipoGestion' && value !== 'Venta Nueva') {
                newState.afiliacion = false;
            }

            if (name === 'contratoOrigenId') {
                if (value) {
                    const con = listaContratosCliente.find(c => c.id === value);
                    if (con) {
                        newState.contratoOrigenId = value;
                        newState.contratoVinculadoId = ''; 
                        newState.nContratoOrigen = con.nContrato || '';
                        newState.nContrato = con.nContrato || '';
                        newState.nContratoVinculado = '';  
                        newState.artActual = con.compania || prev.artActual;
                        newState.artActualId = con.companiaId || '';
                        newState.vendedor = con.vendedor || prev.vendedor;
                        newState.responsableAsignado = con.responsableAsignado || prev.responsableAsignado || '';
                        newState.ciiu = con.ciiu || '';
                        newState.alicuotaActual = con.alicuota ? formatPercentForInput(con.alicuota) : prev.alicuotaActual;
                        
                        buscarNegociacionesPrevias(value);
                    }
                } else {
                    newState.contratoOrigenId = '';
                    newState.contratoVinculadoId = '';
                    newState.nContratoOrigen = '';
                    newState.nContrato = '';
                    newState.nContratoVinculado = '';
                    newState.artActual = '';
                    newState.artActualId = '';
                    newState.vendedor = '';
                    newState.responsableAsignado = '';
                    newState.ciiu = '';
                    newState.alicuotaActual = '';
                    setPrevGestiones([]);
                }
            }
            
            if (name === 'artActual') {
                const found = companiasART.find(c => c.nombre === value);
                newState.artActualId = found ? found.id : '';
            }
            
            return newState;
        });
    };

    const handlePercentChange = (e) => {
        const { name, value } = e.target;
        let val = value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
        const parts = val.split(',');
        if (parts.length > 2) val = parts[0] + ',' + parts.slice(1).join('');
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handleNumericOnly = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value.replace(/\D/g, '') }));

    const handleCurrencyChange = (e) => {
        let val = e.target.value.replace(/\D/g, ''); 
        setFormData(prev => ({ ...prev, masaSalarial: val ? formatCurrencyForInput(Number(val) / 100) : '' }));
    };

    const handleCotizacionChange = (index, field, value) => {
        const newCots = [...cotizaciones];
        if (field === 'alicuotaCotizada') {
            let val = value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
            const parts = val.split(',');
            if (parts.length > 2) val = parts[0] + ',' + parts.slice(1).join('');
            newCots[index][field] = val;
        } else if (field === 'compania') {
            const found = companiasART.find(c => c.nombre === value);
            newCots[index].compania = value;
            newCots[index].companiaId = found ? found.id : '';
        } else {
            newCots[index][field] = value;
        }
        setCotizaciones(newCots);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        setErrorMsg('');

        try {
            const idClienteFinal = clientInfo?.id || propsInitialData?.clienteId;
            
            // SE EXCLUYE EXPLÍCITAMENTE numeroEndoso PARA QUE NO SE GUARDE EN FIRESTORE
            const { 
                path, 
                _entidadFull, 
                vendedorAsociadoNombre, 
                vendedorAsociado,
                numeroEndoso, // ◄ EXTRAÍDO Y DESCARTADO
                ...cleanedFormData 
            } = formData;

            const docId = propsInitialData?.id || stateData.cotizacionId;
            const numeroDeContrato = formData.nContrato || formData.nContratoOrigen || stateData.nContrato || stateData.nContratoOrigen || '';
            const numeroDeEndoso = formData.numeroEndoso || stateData.numeroEndoso || stateData.nEndoso || '';

            const payload = {
                ...cleanedFormData,
                afiliacion: formData.tipoGestion === 'Venta Nueva' ? Boolean(formData.afiliacion) : false,
                ciiu: formData.ciiu || '',
                masaSalarial: parseCurrency(formData.masaSalarial),
                alicuotaActual: parsePercent(formData.alicuotaActual),
                vendedor: formData.vendedor || '',
                responsableAsignado: formData.responsableAsignado || '',
                cotizaciones: cotizaciones.filter(c => c.compania).map(c => ({
                    ...c, 
                    alicuotaCotizada: parsePercent(c.alicuotaCotizada)
                })),
                clienteId: idClienteFinal,
                clienteNombre: (clientInfo?.nombre || '').trim().toUpperCase(),
                clienteApellido: clientInfo?.persona === 'JURIDICA' ? '' : (clientInfo?.apellido || '').trim().toUpperCase(),
                clienteCuit: clientInfo?.cuit || '',
                clienteDni: clientInfo?.dni || '',
                canal: clientInfo?.canal || formData.canal || '',
                canalNombre: clientInfo?.canalNombre || formData.canalNombre || '',
                canalNombreId: clientInfo?.canalNombreId || formData.canalNombreId || '',
                subcanalNombre: clientInfo?.subcanalNombre || formData.subcanalNombre || '',
                subcanalNombreId: clientInfo?.subcanalNombreId || formData.subcanalNombreId || '',
                tipoEntidad: isProspect ? 'PROSPECTO' : 'CLIENTE',

                // Persistencia de contrato
                contratoOrigenId: formData.contratoOrigenId || stateData.contratoId || '',
                nContratoOrigen: numeroDeContrato,
                nContrato: numeroDeContrato,

                // Campos del aumento
                mesAumento: formData.mesAumento || stateData.mesAumento || '-',
                periodoArca: formData.periodoArca || stateData.periodoArca || '-',
                alicuotaAumentada: parsePercent(formData.alicuotaAumentada || stateData.alicuotaAumentada || 0),
                porcentajeAumento: parsePercent(formData.porcentajeAumento || stateData.porcentajeAumento || 0),
                
                // ÚNICO CAMPO PERSISTIDO PARA ENDOSO
                nEndoso: (numeroDeEndoso && numeroDeEndoso !== '-') ? numeroDeEndoso : '-'
            };

            const dbPath = `public/data/${isProspect ? 'prospectos' : 'clientes'}/${idClienteFinal}/cotizaciones_art`;

            const result = await saveToFirestore(dbPath, payload, docId || null);

            // VINCULACIÓN BIDIRECCIONAL CON EL ENDOSO (TANTO EN CREACIÓN COMO EN EDICIÓN)
            const targetCotId = docId || result.id;
            const targetEndosoId = payload.endosoVinculadoId;
            const targetContratoId = payload.contratoOrigenId;

            if (payload.tipoGestion === 'Aumento de Tarifa' && targetEndosoId && targetContratoId) {
                try {
                    const endRef = doc(db, `public/data/clientes/${idClienteFinal}/contratos/${targetContratoId}/aumentos_art/${targetEndosoId}`);
                    await updateDoc(endRef, { 
                        estado: 'Cotizado', 
                        cotizacionVinculadaId: targetCotId, 
                        cotizacionVinculada: payload.numeroCotizacion 
                    });
                } catch (errVinculo) {
                    console.error("Error al actualizar estado en documento de aumento vinculado:", errVinculo);
                }
            }

            if (onSuccess) onSuccess('Cotización guardada correctamente.');
            else navigate(-1);
        } catch (error) { 
            console.error("Error al guardar cotización:", error);
            setErrorMsg("No se pudo guardar la cotización: " + error.message);
        } finally { 
            setIsSubmitting(false); 
        }
    };

    const isGestionBlocked = isFromEndoso || formData.tipoGestion === 'Aumento de Tarifa';

    return (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-fade-in max-w-5xl mx-auto text-left">
            <div className="bg-orange-50 px-8 py-6 border-b border-orange-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-left">
                <div className="flex items-center gap-4 text-left">
                    <button type="button" onClick={handleCancel} className="p-2 hover:bg-white/50 rounded-full transition-colors text-orange-800 border border-transparent hover:border-orange-200"><IconArrowLeft /></button>
                    <div className="text-left">
                        <h2 className="text-xl font-bold text-orange-900 flex items-center gap-2 uppercase tracking-tighter">
                            { (isEditMode || propsInitialData) ? 'Editar Cotización' : 'Nueva Cotización' } ART
                            <span className="text-sm font-normal text-orange-700 bg-orange-100 px-2 py-0.5 rounded-md border border-orange-200 uppercase tracking-widest">{isProspect ? 'Prospecto' : 'Cliente'}</span>
                        </h2>
                        <p className="font-bold text-lg text-orange-800 uppercase mt-1">
                            {clientInfo?.nombre} {clientInfo?.apellido} <span className="mx-2 text-orange-300">|</span> <span className="font-mono text-sm">{clientInfo?.cuit || clientInfo?.dni}</span>
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8 text-left">
                {errorMsg && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl font-medium text-sm flex items-center gap-3 animate-fade-in">
                        <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
                        {errorMsg}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-lg border border-gray-200 text-left">
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">N° Cotización</label>
                        <input type="text" value={formData.numeroCotizacion} readOnly className="input-round bg-white font-mono text-center font-bold text-orange-600 border-orange-200 shadow-none cursor-default" />
                    </div>
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Fecha Carga</label>
                        <input type="date" name="fechaCarga" value={formData.fechaCarga} onChange={handleChange} className="input-round text-left" required />
                    </div>
                    <div className="text-left">
                        <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-tighter text-left">Estado General</label>
                        <select name="estado" value={formData.estado} onChange={handleChange} className={`input-round border-slate-200 transition-all cursor-pointer font-bold text-left ${getCotizacionColor(formData.estado)}`} required>
                            {EstadoCotizacionArt.map(e => <option key={e} value={e} className="bg-white text-gray-700">{e}</option>)}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div className="text-left">
                        <label className="block text-sm font-bold text-gray-600 mb-1 text-left">Tipo de Gestión *</label>
                        <div className="flex items-center gap-4">
                            <select name="tipoGestion" value={formData.tipoGestion} onChange={handleChange} className="input-round font-bold text-left border-slate-200 flex-1" required disabled={isGestionBlocked && !isEditMode}>
                                {isGestionBlocked ? (
                                    <option value="Aumento de Tarifa">Aumento de Tarifa</option>
                                ) : (
                                    <>
                                        <option value="">Seleccione...</option>
                                        <option value="Venta Nueva">Venta Nueva</option>
                                        <option value="Recotización de Cliente">Recotización de Cliente</option>
                                    </>
                                )}
                            </select>

                            {formData.tipoGestion === 'Venta Nueva' && (
                                <label className="flex items-center gap-2 bg-orange-50/80 border border-orange-200 px-3 py-2.5 rounded-xl cursor-pointer hover:bg-orange-100/50 transition-colors shrink-0 animate-fade-in">
                                    <input 
                                        type="checkbox" 
                                        name="afiliacion" 
                                        checked={Boolean(formData.afiliacion)} 
                                        onChange={handleChange} 
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                                    />
                                    <span className="text-xs font-extrabold text-orange-900 uppercase tracking-wider select-none">
                                        Afiliación
                                    </span>
                                </label>
                            )}
                        </div>
                    </div>

                    {(formData.tipoGestion === 'Recotización de Cliente' || formData.tipoGestion === 'Aumento de Tarifa') && (
                        <div className="animate-fade-in text-left">
                            <label className="block text-sm font-bold text-gray-600 mb-1 text-left">
                                Contrato Vinculado de Origen *
                            </label>
                            {isGestionBlocked ? (
                                <input 
                                    type="text" 
                                    value={formData.nContrato ? `Contrato #${formData.nContrato} (${formData.compania || formData.artActual || 'S/D'})` : 'No seleccionado'} 
                                    readOnly 
                                    className="input-round bg-blue-50/50 border-blue-200 font-bold text-blue-800 cursor-default text-left" 
                                />
                            ) : isLoadingContratos ? (
                                <div className="input-round bg-blue-50/30 border-blue-100 flex items-center px-4 text-blue-400 text-xs italic">
                                    <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Cargando contratos del cliente...
                                </div>
                            ) : listaContratosCliente.length === 0 ? (
                                <div className="input-round bg-red-50/50 border-red-200 flex items-center px-4 text-red-500 text-xs italic font-bold">
                                    El cliente no posee ningún contrato activo cargado para poder recotizar.
                                </div>
                            ) : (
                                <select
                                    name="contratoOrigenId"
                                    value={formData.contratoOrigenId || ''}
                                    onChange={handleChange}
                                    className="input-round border-blue-200 font-bold text-blue-800 bg-blue-50/10 focus:border-blue-400 focus:bg-white text-left"
                                    required
                                >
                                    <option value="">Seleccione un contrato...</option>
                                    {listaContratosCliente.map(c => (
                                        <option key={c.id} value={c.id}>
                                            Contrato #{c.nContrato} - {c.compania || 'S/D'} (Alic: {c.alicuota || '0'}%)
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}
                </div>

                <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/5 italic text-left">
                    <legend className="px-3 text-lg font-bold text-orange-700 bg-white ml-4 rounded-lg text-left">Datos del Riesgo</legend>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-5 pt-2 text-left items-end">
                        <div className="lg:col-span-2 text-left">
                            <label className="label-form text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 text-left">CIIU (Actividad)</label>
                            <input type="text" name="ciiu" value={formData.ciiu} onChange={handleNumericOnly} className="input-round font-mono text-gray-800 border-slate-200 text-left" placeholder="Solo números" />
                        </div>
                        <div className="text-left">
                            <label className="label-form text-[10px] font-black text-gray-400 uppercase mb-1 tracking-widest text-center">Cápitas</label>
                            <input type="number" name="capitas" value={formData.capitas} onChange={handleChange} className="input-round border-slate-200 text-center font-bold" placeholder="0" />
                        </div>
                        <div className="lg:col-span-2 text-left">
                            <CurrencyInput 
                                label="Masa Salarial ($)"
                                value={formData.masaSalarial}
                                onChange={handleCurrencyChange}
                                focusColor="focus-within:border-orange-500"
                            />
                        </div>
                        <div className="text-left">
                            <PercentInput 
                                label="Alic. Act. (%)"
                                name="alicuotaActual"
                                value={formData.alicuotaActual}
                                onChange={handlePercentChange}
                                focusColor="focus-within:border-orange-500"
                            />
                        </div>
                        
                        <div className="lg:col-span-2 text-left">
                            <label className="label-form text-sm font-bold text-gray-600 mb-1 text-left">Aseguradora Actual</label>
                            <select name="artActual" value={formData.artActual} onChange={handleChange} className="input-round border-slate-200 uppercase font-bold text-blue-700 text-left">
                                <option value="">Seleccione...</option>
                                {companiasART.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                            </select>
                        </div>
                        <div className="lg:col-span-2 text-left">
                            <label className="label-form text-sm font-bold text-gray-600 mb-1 text-left">Vendedor / Comercial</label>
                            <select name="vendedor" value={formData.vendedor} onChange={handleChange} className="input-round border-slate-200 font-bold text-left">
                                <option value="">Seleccione...</option>
                                {vendedores.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <div className="lg:col-span-2 text-left">
                            <label className="label-form text-sm font-bold text-blue-600 mb-1 text-left">Responsable Asignado</label>
                            <select name="responsableAsignado" value={formData.responsableAsignado || ''} onChange={handleChange} className="input-round border-blue-200 font-bold text-left bg-blue-50/10 focus:bg-white focus:border-blue-400">
                                <option value="">Seleccione responsable...</option>
                                {vendedores.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                </fieldset>

                {prevGestiones.length > 0 && (
                    <div className="space-y-4 text-left">
                        <div className="flex items-center justify-between border-b border-blue-100 pb-2 text-left">
                            <h3 className="text-xs font-black text-blue-700 uppercase tracking-[0.2em] flex items-center gap-2 italic text-left"><span className="w-2 h-4 bg-blue-600 rounded-full"></span> Ofertas Anteriores</h3>
                            <button type="button" onClick={() => { setShowImportSelector(!showImportSelector); }} className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full hover:bg-blue-100 transition-all border border-blue-100"> {showImportSelector ? 'Ocultar' : 'Ver historial'} </button>
                        </div>
                        {showImportSelector && (
                            <div className="bg-white rounded-xl border-2 border-blue-100 shadow-xl overflow-hidden divide-y divide-blue-50 text-left">
                                {prevGestiones.map((o, idx) => (
                                    <div key={idx} className="p-4 flex items-center justify-between hover:bg-blue-50/30 transition-colors text-left">
                                        <div className="flex-1 text-left">
                                            <div className="flex items-center gap-4 text-left">
                                                <span className="text-[11px] font-black text-blue-600 uppercase bg-blue-50 px-2 py-0.5 rounded border border-blue-100 tracking-tighter">{o.compania}</span>
                                                <span className="text-base font-black text-slate-800 tracking-tight">Alícuota: {formatPercentForInput(o.alicuotaCotizada)}%</span>
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-1 italic tracking-wide text-left">Origen: {o.parentTipo} ({new Date(o.parentFecha).toLocaleDateString('es-AR')})</p>
                                        </div>
                                        <button type="button" onClick={() => handleSelectOferta(o)} className="px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase rounded-lg hover:bg-blue-700 shadow-md">Importar</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <fieldset className="border border-blue-200 rounded-xl p-6 relative mt-6 bg-blue-50/20 italic text-left">
                    <legend className="px-3 text-lg font-bold text-blue-700 bg-white absolute -top-3 left-4 rounded-lg text-left">Comparativa de Ofertas</legend>
                    <div className="space-y-4 pt-2 text-left">
                        {cotizaciones.map((cot, index) => (
                            <div key={index} className="bg-white p-5 rounded-xl border border-blue-100 shadow-sm relative overflow-visible text-left">
                                <div className="grid grid-cols-12 gap-4 items-end text-left">
                                    <div className="col-span-12 md:col-span-3 text-left">
                                        <label className="label-form text-[10px] font-black text-gray-400 mb-1 uppercase tracking-widest text-left">Compañía</label>
                                        <select value={cot.compania} onChange={(e) => handleCotizacionChange(index, 'compania', e.target.value)} className="input-round border-slate-200 font-bold uppercase text-slate-700 text-left">
                                            <option value="">Seleccione...</option>
                                            {companiasART.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-12 md:col-span-3 text-left">
                                        <label className="label-form text-[10px] font-black text-gray-400 mb-1 uppercase tracking-widest text-left pl-1">Estado Fila</label>
                                        <select value={cot.estadoCotizacion} onChange={(e) => handleCotizacionChange(index, 'estadoCotizacion', e.target.value)} className="input-round border-slate-200 text-sm font-bold text-blue-600 cursor-pointer appearance-none text-left">
                                            <option value="Cotizada">Cotizada (OK)</option>
                                            <option value="Solicitada">Solicitada</option>
                                            <option value="Bloqueada">Bloqueada</option>
                                            <option value="No cotiza">No cotiza</option>
                                        </select>
                                    </div>
                                    <div className="col-span-6 md:col-span-2 text-left">
                                        <PercentInput 
                                            label="Alic. %"
                                            value={cot.alicuotaCotizada}
                                            onChange={(e) => handleCotizacionChange(index, 'alicuotaCotizada', e.target.value)}
                                            disabled={['Solicitada', 'Bloqueada', 'No cotiza'].includes(cot.estadoCotizacion)}
                                            placeholder="0,00"
                                            focusColor="focus-within:border-orange-500"
                                            textColor="text-center font-black text-blue-800 text-lg"
                                        />
                                    </div>
                                    <div className="col-span-6 md:col-span-3 text-left">
                                        <label className="block text-[10px] font-black text-gray-400 mb-1 uppercase tracking-widest text-center">Bloqueo</label>
                                        <div className="flex items-center gap-2 h-[46px] bg-gray-50/50 rounded-xl px-2 border border-dashed border-gray-200 text-left">
                                            <input type="checkbox" checked={cot.tieneFechaBloqueo} onChange={(e) => handleCotizacionChange(index, 'tieneFechaBloqueo', e.target.checked)} className="w-4 h-4 text-blue-600 rounded" />
                                            <input type="date" value={cot.fechaBloqueo || ''} onChange={(e) => handleCotizacionChange(index, 'fechaBloqueo', e.target.value)} className="bg-transparent text-xs w-full outline-none font-bold text-slate-600 text-left" disabled={!cot.tieneFechaBloqueo} />
                                        </div>
                                    </div>
                                    <div className="col-span-12 md:col-span-1 flex justify-end">
                                        <button type="button" onClick={() => setCotizaciones(cotizaciones.filter((_, i) => i !== index))} className="text-slate-300 hover:text-red-600 p-2 font-bold text-xl transition-colors">✕</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button type="button" onClick={() => setCotizaciones([...cotizaciones, { compania: '', companiaId: '', estadoCotizacion: 'Cotizada', tieneFechaBloqueo: false, fechaBloqueo: '' }])} className="text-[10px] font-black text-blue-600 bg-white px-4 py-2.5 rounded-lg border-2 border-dashed border-blue-200 hover:bg-blue-50 uppercase tracking-widest italic transition-colors text-left">+ Agregar compañía</button>
                    </div>
                </fieldset>

                <div className="pt-4 text-left">
                    <label className="block text-sm font-bold text-gray-600 mb-2 text-left">Observaciones de la Negociación</label>
                    <textarea name="observaciones" rows="3" value={formData.observaciones} onChange={handleChange} className="input-round border-slate-200 h-auto min-h-[120px] resize-none p-4 text-sm leading-relaxed text-left" placeholder="Notas sobre la gestión comercial..."></textarea>
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t border-gray-100 text-left">
                    <button type="button" onClick={handleCancel} className="px-6 py-3 rounded-xl border border-gray-300 font-bold text-gray-500 hover:bg-slate-50 uppercase text-[10px] tracking-widest transition shadow-sm text-left">Cancelar</button>
                    <button type="submit" disabled={isSubmitting} className="px-8 py-3 rounded-lg bg-orange-600 text-white font-bold hover:bg-orange-700 shadow-lg disabled:opacity-50 uppercase text-[10px] tracking-widest transform hover:scale-105 transition-all shadow-orange-200 text-left">
                        {isSubmitting ? 'Guardando...' : ( (isEditMode || propsInitialData) ? 'Actualizar Cotización' : 'Confirmar Registro' )}
                    </button>
                </div>
            </form>

            <style>{`
                .input-round { width: 100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e5e7eb; outline: none; transition: all 0.2s; font-size: 0.95rem; background-color: #fff; height: 46px; text-align: left; }
                .input-round:focus { border-color: #f97316; box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1); }
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                select.input-round { text-align-last: left; }
            `}</style>
        </div>
    );
};

export default FormularioCotizacionArt;